import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { companyService, type PickupLocation } from '../../services';
import { gtagService } from '../../services/gtagService';

interface PickupLocationSelectorProps {
  selectedLocationId: string | null;
  transportProvider?: string;
  onLocationSelect: (locationId: string, locationName?: string) => void;
}

export const PickupLocationSelector: React.FC<PickupLocationSelectorProps> = ({
  selectedLocationId,
  transportProvider,
  onLocationSelect,
}) => {
  const [allLocations, setAllLocations] = useState<PickupLocation[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  // Filter locations locally based on search query
  const displayedLocations = useMemo(() => {
    if (!searchQuery.trim()) {
      return allLocations;
    }

    const query = searchQuery.toLowerCase();
    return allLocations.filter(location => {
      return (
        (location.name?.toLowerCase() || '').includes(query) ||
        (location.address?.toLowerCase() || '').includes(query) ||
        (location.area?.toLowerCase() || '').includes(query) ||
        (location.city?.toLowerCase() || '').includes(query) ||
        (location.state?.toLowerCase() || '').includes(query) ||
        (location.landmark?.toLowerCase() || '').includes(query) ||
        (location.phone || '').includes(query) ||
        (location.transportProviderName?.toLowerCase() || '').includes(query)
      );
    });
  }, [allLocations, searchQuery]);

  // Load all pickup locations from backend
  const loadPickupLocations = useCallback(async () => {
    if (!transportProvider) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const params = {
        transportProviderName: transportProvider,
      };
      
      const response = await companyService.getPickupLocations(params);
      
      if (response.success) {
        setAllLocations(response.data);
      } else {
        setError('Failed to load pickup locations');
      }
    } catch {
      setError('Error loading pickup locations');
    } finally {
      setLoading(false);
    }
  }, [transportProvider]);

  // Load pickup locations based on transport provider
  useEffect(() => {
    if (transportProvider) {
      loadPickupLocations();
    } else {
      // Clear locations if no transport provider
      setAllLocations([]);
    }
  }, [transportProvider, loadPickupLocations]);

  // Handle search input change
  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
  };

  if (!transportProvider) {
    return (
      <div className="text-center py-8 text-gray-500">
        Please select a transport provider to view pickup locations
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Search Bar */}
      <div className="space-y-2">
        <div className="relative">
          <input
            type="text"
            placeholder="Search by location name, area, or city..."
            value={searchQuery}
            onChange={(e) => handleSearchChange(e.target.value)}
            className="w-full px-4 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-500 focus:border-accent-500"
          />
          <svg
            className="absolute right-3 top-2.5 h-5 w-5 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
        </div>
        
        {/* Search status */}
        {searchQuery && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-2">
            <p className="text-sm text-blue-800">
              {loading 
                ? `Loading locations...` 
                : `Found ${displayedLocations.length} location${displayedLocations.length !== 1 ? 's' : ''} matching "${searchQuery}"`
              }
            </p>
          </div>
        )}
      </div>

      {/* Loading State */}
      {loading && (
        <div className="text-center py-8">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-accent-600"></div>
          <p className="mt-2 text-gray-600">Loading pickup locations...</p>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
          {error}
        </div>
      )}

      {/* Locations List */}
      {!loading && !error && (
        <>
          <div className="space-y-3 pb-4">
            {displayedLocations.map((location) => {
              const isSelected = selectedLocationId === location.id;
              
              return (
                <div
                  key={location.id}
                  onClick={() => {
                    onLocationSelect(location.id, location.name);
                    // Track pickup location selection
                    gtagService.trackPickupLocationSelect(location.name);
                  }}
                  className={`
                    relative rounded-lg border-2 p-4 transition-all overflow-hidden cursor-pointer
                    ${isSelected
                      ? 'border-accent-500 bg-accent-50'
                      : 'border-gray-200 hover:border-gray-300 bg-white'
                    }
                  `}
                >
                {/* Radio Button */}
                <div className="absolute top-4 left-4">
                  <div className={`
                    w-5 h-5 rounded-full border-2 flex items-center justify-center
                    ${isSelected ? 'border-accent-500' : 'border-gray-300'}
                  `}>
                    {isSelected && (
                      <div className="w-3 h-3 rounded-full bg-accent-500"></div>
                    )}
                  </div>
                </div>

                {/* Location Details */}
                <div className="pl-8 pr-4">
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-gray-900 pr-2">
                        {location.name}
                      </h3>
                      <p className="text-sm text-gray-600 mt-1">
                        {location.address}
                      </p>
                      <div className="flex flex-wrap items-center gap-2 sm:gap-4 mt-2 text-xs text-gray-500">
                        <span className="flex items-center gap-1">
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                          </svg>
                          {location.city}
                        </span>
                        <span className="flex items-center gap-1">
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                          </svg>
                          {location.phone}
                        </span>
                        <span className="flex items-center gap-1">
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          {location.timings}
                        </span>
                        {location.transportProviderName && (
                          <span className="flex items-center gap-1">
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            {location.transportProviderName}
                          </span>
                        )}
                      </div>
                    </div>

                  </div>
                </div>

                {/* Selected Badge */}
                <div className="absolute top-2 right-2 sm:top-4 sm:right-4">
                  {isSelected && (
                    <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-accent-100 text-accent-700">
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      Selected
                    </span>
                  )}
                </div>
              </div>
              );
            })}
          </div>


          {/* No Results */}
          {displayedLocations.length === 0 && !loading && (
            <div className="text-center py-8 text-gray-500">
              <svg
                className="mx-auto h-12 w-12 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                />
              </svg>
              <p className="mt-2 font-medium">No pickup locations found</p>
              {!searchQuery && (
                <p className="text-sm mt-2">
                  No pickup locations available for {transportProvider}
                </p>
              )}
              {searchQuery && (
                <p className="text-sm mt-2">
                  Try adjusting your search or{' '}
                  <button
                    type="button"
                    onClick={() => setSearchQuery('')}
                    className="text-accent-600 hover:text-accent-700"
                  >
                    clear the search
                  </button>
                </p>
              )}
            </div>
          )}
        </>
      )}

      {/* Helper Text */}
      {!loading && allLocations.length > 0 && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-sm text-gray-600 mt-4">
          <p>
            {searchQuery 
              ? `Showing ${displayedLocations.length} of ${allLocations.length} locations`
              : `${allLocations.length} pickup location${allLocations.length !== 1 ? 's' : ''} available for ${transportProvider}`
            }
          </p>
        </div>
      )}
    </div>
  );
};